import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/auth/useAuth'
import { api } from '@/lib/api'

const tk = (tenantId: string, ...parts: unknown[]) => ['tenant', tenantId, 'payroll', ...parts]

function makeQuery<T>(path: string) {
  return () => {
    const { user } = useAuth()
    const tenantId = user?.tenantId
    return useQuery({
      queryKey: tk(tenantId ?? 'anon', path),
      queryFn: () => api.get<{ data: T[] }>(path),
      enabled: Boolean(tenantId),
      staleTime: 0,
      refetchOnMount: 'always',
    })
  }
}

export const useTimesheets = makeQuery<{
  id: string; status: string; created_at: string; approved_at: string | null
  pay_period_id: string | null; period_start: string | null; period_end: string | null
  employee_id: string; first_name: string; last_name: string; employee_number: string
  total_hours: string
}>('/api/payroll/timesheets')

export const useLeaveRequests = makeQuery<{
  id: string; start_date: string; end_date: string; hours_requested: string; status: string
  leave_type: string; leave_type_label: string
  employee_id: string; first_name: string; last_name: string; employee_number: string
  approved_at: string | null
}>('/api/payroll/leave-requests')

export const useLeaveTypes = makeQuery<{
  id: string; slug: string; label: string; is_paid: boolean
  accrual_rate_hours: string; accrual_period: string
  max_balance_hours: string | null; rollover_kind: string
  cashout_allowed: boolean; requires_approval: boolean
}>('/api/payroll/leave-types')

export const useHolidays = makeQuery<{
  id: string; holiday_date: string; label: string; is_paid: boolean; pay_multiplier: string
  calendar_name: string; country: string; state_region: string | null
}>('/api/payroll/holidays')

export const useShifts = makeQuery<{
  id: string; name: string; shift_kind: string; start_time: string; end_time: string
  crosses_midnight: boolean; break_minutes: number; paid_break_minutes: number
  timezone: string; shift_diff_pct: string; assigned_count: number
}>('/api/payroll/shifts')

export const useSalaryStructures = makeQuery<{
  id: string; name: string; description: string | null; currency: string
  is_template: boolean; component_count: number; created_at: string
}>('/api/payroll/salary-structures')

export const usePayGrades = makeQuery<{
  id: string; grade_code: string; label: string
  min_minor_units: string; mid_minor_units: string; max_minor_units: string
  currency: string; effective_from: string; effective_to: string | null
}>('/api/payroll/pay-grades')

export const useEquityGrants = makeQuery<{
  id: string; grant_number: string; grant_type: string; shares: string
  strike_price_minor_units: string | null; strike_currency: string | null
  fmv_minor_units: string | null; fmv_currency: string | null
  grant_date: string; cliff_months: number | null; total_months: number | null; status: string
  employee_id: string; first_name: string; last_name: string; employee_number: string
  vested_tranches: number; total_tranches: number
}>('/api/payroll/equity-grants')

export const useBenefitPlans = makeQuery<{
  id: string; plan_code: string; plan_name: string; plan_kind: string
  carrier: string | null; policy_number: string | null; currency: string
  employer_share_pct: string | null; employee_share_pct: string | null
  pretax: boolean; is_active: boolean; effective_from: string
}>('/api/payroll/benefit-plans')

export const useBenefitEnrollments = makeQuery<{
  id: string; coverage_tier: string
  employer_amount_minor_units: string; employee_amount_minor_units: string; currency: string
  effective_from: string; effective_to: string | null
  enrollment_window_kind: string; status: string
  plan_code: string; plan_name: string; plan_kind: string
  employee_id: string; first_name: string; last_name: string
}>('/api/payroll/benefit-enrollments')

export const useRetirementAccounts = makeQuery<{
  id: string; account_kind: string; provider: string | null; account_number_last4: string | null
  ytd_employee_contrib_minor_units: string; ytd_employer_contrib_minor_units: string
  ytd_catchup_minor_units: string; annual_limit_minor_units: string | null
  catchup_eligible: boolean
  employee_id: string; first_name: string; last_name: string; employee_number: string
}>('/api/payroll/retirement')

export const useHsaFsaAccounts = makeQuery<{
  id: string; account_kind: string
  annual_election_minor_units: string; ytd_contrib_minor_units: string; ytd_disbursed_minor_units: string
  currency: string; effective_from: string; effective_to: string | null
  employee_id: string; first_name: string; last_name: string; employee_number: string
}>('/api/payroll/hsa-fsa')

export const useAcaPeriods = makeQuery<{
  id: string; period_kind: string; period_start: string; period_end: string
  hours_total: string; full_time_status: boolean
  offer_kind: string | null; affordability_safe_harbor: string | null
  employee_id: string; first_name: string; last_name: string; employee_number: string
}>('/api/payroll/aca')

export const useNachaBatches = makeQuery<{
  id: string; batch_number: number; sec_code: string; service_class_code: string
  effective_entry_date: string; entry_count: number
  debit_total_minor_units: string; credit_total_minor_units: string
  transmitted_at: string | null; acknowledged_at: string | null; settled_at: string | null
  status: string; originator_name: string; company_name: string; run_number: string | null
}>('/api/payroll/nacha-batches')

export const useChecks = makeQuery<{
  id: string; check_number: string; payee_name: string
  amount_minor_units: string; currency: string
  issued_date: string; voided_at: string | null; voided_reason: string | null
  cleared_at: string | null
}>('/api/payroll/checks')

export const useGarnishments = makeQuery<{
  id: string; order_number: string | null; kind: string; court_priority: number
  per_period_amount_minor_units: string | null; per_period_pct: string | null
  balance_owed_minor_units: string | null; remit_to_name: string; status: string
  currency: string; effective_from: string
  employee_id: string; first_name: string; last_name: string; employee_number: string
}>('/api/payroll/garnishments')

export const usePaymentInstructions = makeQuery<{
  id: string; method: string; amount_minor_units: string; currency: string
  status: string; return_code: string | null; return_reason: string | null
  transmitted_at: string | null; settled_at: string | null
  check_number: string | null; trace_number: string | null
  employee_id: string | null; first_name: string | null; last_name: string | null
  contractor_id: string | null; business_name: string | null
  individual_first_name: string | null; individual_last_name: string | null
}>('/api/payroll/payment-instructions')

export const useOnboarding = makeQuery<{
  id: string; employee_number: string; first_name: string; last_name: string
  email: string | null; hire_date: string; status: string; work_state: string | null
  has_i9: number; has_w4: number; has_dd: number; bank_count: number
}>('/api/payroll/onboarding')

export const useCompCycles = makeQuery<{
  id: string; name: string; status: string; period_start: string; period_end: string
}>('/api/payroll/comp-cycles')

export const usePayrollReportCatalog = makeQuery<{
  slug: string; name: string; description: string; endpoint: string; required_params: string[]
}>('/api/payroll/reports')

export const useHolidayCalendars = makeQuery<{
  id: string; name: string; country: string; state_region: string | null
}>('/api/payroll/holiday-calendars')

export const useNachaOriginators = makeQuery<{
  id: string; name: string; odfi_routing_number: string; company_name: string; is_active: boolean
}>('/api/payroll/nacha-originators')

export const addAttendance = (b: Record<string, unknown>) => api.post('/api/payroll/attendance', b)
export const createLeaveRequest = (b: Record<string, unknown>) => api.post('/api/payroll/leave-requests', b)
export const decideLeaveRequest = (id: string, decision: 'approve' | 'reject') => api.post(`/api/payroll/leave-requests/${id}/${decision}`, {})
export const createShift = (b: Record<string, unknown>) => api.post('/api/payroll/shifts', b)
export const createHolidayCalendar = (b: Record<string, unknown>) => api.post('/api/payroll/holiday-calendars', b)
export const createHoliday = (b: Record<string, unknown>) => api.post('/api/payroll/holidays', b)
export const createLeaveType = (b: Record<string, unknown>) => api.post('/api/payroll/leave-types', b)
export const createEquityGrant = (b: Record<string, unknown>) => api.post('/api/payroll/equity-grants', b)
export const createBenefitPlan = (b: Record<string, unknown>) => api.post('/api/payroll/benefit-plans', b)
export const createBenefitEnrollment = (b: Record<string, unknown>) => api.post('/api/payroll/benefit-enrollments', b)
export const createRetirementAccount = (b: Record<string, unknown>) => api.post('/api/payroll/retirement', b)
export const createHsaFsaAccount = (b: Record<string, unknown>) => api.post('/api/payroll/hsa-fsa', b)
export const createGarnishment = (b: Record<string, unknown>) => api.post('/api/payroll/garnishments', b)
export const createNachaOriginator = (b: Record<string, unknown>) => api.post('/api/payroll/nacha-originators', b)
export const createSalaryStructure = (b: Record<string, unknown>) => api.post('/api/payroll/salary-structures', b)
export const createPayGrade = (b: Record<string, unknown>) => api.post('/api/payroll/pay-grades', b)
